import React, { useState, useEffect } from 'react'
import { RadioGroup, Radio, MenuOverlay, Button, ListItem, Checkbox, Row, Separator, H5, Paragraph } from '@fs/zion-ui'
import { DocumentBasic, DocumentImage, Menu, Person } from '@fs/zion-icon'
import { useSlsStateContext } from '@fs/zion-sls'
import { getPersonFullName } from '../../../../../shared/utils/utils'
import styles from '../../DebugViewer.styles'
import HistoryTable from './HistoryTable'

export default function HistoryTableView({ actions, arkPath, title, changeTitle }) {
  const state = useSlsStateContext()

  const [activeHistory, setActiveHistory] = useState()
  const [showRaw, setShowRaw] = useState(false)
  const [checked, setChecked] = useState(false)

  useEffect(() => {
    setActiveHistory(
      actions?.sort((a, b) => b?.journalAttribution?.timestamp || 0 - (a?.journalAttribution?.timestamp || 0))
    )
  }, [actions])

  const compare = (a, b) => {
    if (a < b) return -1
    if (a > b) return 1
    return 0
  }

  const changeHistory = (id, text) => {
    changeTitle(text)
    setChecked(false)
    if (id === arkPath) {
      setActiveHistory(
        actions?.sort((a, b) => (b?.journalAttribution?.timestamp || 0) - (a?.journalAttribution?.timestamp || 0))
      )
    } else {
      const filteredHistory = actions?.filter(
        (item) => item.personId[0] === id || item.recordId[0] === id || item.id === id
      )
      setActiveHistory(filteredHistory)
    }
  }

  const handleCheck = ({ target }) => {
    setChecked(target.checked)
    if (target.checked) {
      setActiveHistory(
        activeHistory
          .filter(
            (item) =>
              item?.journalAttribution?.userId && item?.journalAttribution?.userId !== 'fs:AutomatedContentExtraction'
          )
          .sort((a, b) => (b?.journalAttribution.timestamp || 0) - (a?.journalAttribution?.timestamp || 0))
      )
    } else {
      setActiveHistory(
        actions?.sort((a, b) => (b?.journalAttribution?.timestamp || 0) - (a?.journalAttribution?.timestamp || 0))
      )
    }
  }

  return (
    <div>
      <Separator size="xxs" />
      <H5>History Table</H5>
      <Paragraph size="sm">
        This is the history of changes that have been made to this image. Click a row to see the request that made the
        change. Or view in the raw list format.
      </Paragraph>
      <Separator size="sm" />
      <div css={styles.controlsRow}>
        <RadioGroup orientation="horizontal">
          <Radio
            checked={!showRaw}
            name="summaryRadio"
            value="false"
            label="Summary"
            onChange={() => setShowRaw(false)}
          />
          <Radio checked={showRaw} name="rawRadio" value="true" label="Raw" onChange={() => setShowRaw(true)} />
          <Checkbox label="User Changes Only" name="userOnly" checked={checked} onChange={handleCheck} />
        </RadioGroup>
        <Row guttersY="none">
          {title !== arkPath && (
            <GroupNav
              groupItems={[{ id: arkPath }]}
              buttonText="Image"
              changeHistory={changeHistory}
              Icon={DocumentImage}
            />
          )}
          {!!state.records?.length && (
            <GroupNav
              groupItems={state.records.sort((a, b) => compare(a.id, b.id))}
              buttonText="Records"
              changeHistory={changeHistory}
              state={state}
              Icon={DocumentBasic}
            />
          )}
          {!!state.persons?.length && (
            <GroupNav
              groupItems={state.persons.sort((a, b) => compare(a.id, b.id))}
              buttonText="Persons"
              changeHistory={changeHistory}
              state={state}
              Icon={Person}
            />
          )}
          {!!state.fields?.length && (
            <GroupNav
              groupItems={state.fields.sort((a, b) => compare(a.id, b.id))}
              buttonText="Fields"
              changeHistory={changeHistory}
              state={state}
            />
          )}
        </Row>
      </div>
      {showRaw && <pre css={styles.rawData}>{JSON.stringify(activeHistory, null, 2)}</pre>}
      {!showRaw && <HistoryTable activeHistory={activeHistory} />}
    </div>
  )
}

function GroupNav({ groupItems, buttonText, changeHistory, state, Icon }) {
  const getPrincipalPerson = (recordId) => {
    const record = state.records.find((r) => r.id === recordId)
    if (record.personIds.length > 0) {
      for (let i = 0; i < record.personIds.length; i++) {
        const person = state.persons.find((p) => p.id === record.personIds[i])
        if (person.primary) return getPersonFullName(record.personIds[i], state)
      }
    }
    return '<Unknown>'
  }

  return (
    <MenuOverlay
      menuButton={
        <Button Icon={Icon || Menu}>
          {buttonText} ({groupItems?.length || 0})
        </Button>
      }
    >
      {!!groupItems?.length &&
        groupItems.map((item) => {
          let text = item.id
          if (item.id.startsWith('1:1')) {
            text = `${getPersonFullName(item.id, state)} (${item.id})`
          }
          if (item.id.startsWith('1:2')) {
            text = `${item.id} (Principal: ${getPrincipalPerson(item.id)})`
          }
          if (item.type === 'FIELD') {
            text = `${item.label} - ${item.value} (${item.id})`
          }
          return <ListItem key={item.id} dense primaryText={text} onClick={() => changeHistory(item.id, text)} />
        })}
    </MenuOverlay>
  )
}
